import { X, Edit3 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Reusable full-screen "expanded record" overlay.
 * Consumers pass an array of `pills` (React nodes) and `detailBlocks`
 * ([{icon, label, value, accent}]) so any focus area can plug in.
 */
export default function ExpandCard({
    open,
    onClose,
    onEdit,
    code, // e.g. "VESSEL #01"
    ribbonExtras, // React node rendered next to code
    title,
    pills, // React node[]
    detailBlocks, // [{icon:Icon, label, value, accent}]
    sections, // [{icon, label, value}]  full-width sections
    testId,
    closeTestId,
    editTestId,
    footerLeft,
}) {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="fixed inset-0 z-[85] flex items-center justify-center bg-black/85 backdrop-blur-md"
                    onClick={onClose}
                    data-testid={testId}
                >
                    <motion.div
                        initial={{ y: 16, scale: 0.98 }}
                        animate={{ y: 0, scale: 1 }}
                        exit={{ y: 16, scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-[1200px] max-w-[92vw] max-h-[86vh] overflow-hidden bg-[#0A0A0A] border-2 border-virtus-orange"
                    >
                        <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-virtus-orange" />
                        <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-virtus-orange" />
                        <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-virtus-orange" />
                        <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-virtus-orange" />

                        <div className="flex items-center justify-between px-8 py-4 border-b border-virtus-line">
                            <div className="flex items-center gap-4">
                                <div className="font-mono text-xs tracking-[0.28em] uppercase text-virtus-orange">
                                    {code}
                                </div>
                                {ribbonExtras}
                            </div>
                            <button
                                onClick={onClose}
                                className="flex items-center gap-2 px-3 py-2 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim hover:text-virtus-orange border border-virtus-line hover:border-virtus-orange"
                                data-testid={closeTestId}
                            >
                                <X size={12} /> CLOSE
                            </button>
                        </div>

                        <div className="px-8 pt-8 pb-4">
                            <h1
                                className="font-heading font-black uppercase tracking-tighter leading-none text-white break-words"
                                style={{ fontSize: "clamp(36px, 5vw, 72px)" }}
                            >
                                {title}
                            </h1>
                        </div>

                        {pills && (
                            <div className="px-8 pb-6 flex items-center gap-6 flex-wrap">
                                {pills.map((p, i) => (
                                    <div key={i} className="scale-[1.5] origin-left">
                                        {p}
                                    </div>
                                ))}
                            </div>
                        )}

                        {detailBlocks && detailBlocks.length > 0 && (
                            <div
                                className={`grid gap-px bg-virtus-line border-t border-b border-virtus-line`}
                                style={{
                                    gridTemplateColumns: `repeat(${detailBlocks.length}, minmax(0, 1fr))`,
                                }}
                            >
                                {detailBlocks.map((d) => (
                                    <div key={d.label} className="bg-virtus-bg p-5">
                                        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint mb-2">
                                            {d.icon ? <d.icon size={12} /> : null}
                                            {d.label}
                                        </div>
                                        <div
                                            className="font-mono text-xl font-bold truncate"
                                            style={{ color: d.accent || "#FFFFFF" }}
                                        >
                                            {d.value || "—"}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {sections && sections.length > 0 && (
                            <div className="px-8 py-6 space-y-4 max-h-[26vh] overflow-y-auto">
                                {sections.map((s) => (
                                    <div key={s.label}>
                                        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim mb-2">
                                            {s.icon ? <s.icon size={12} /> : null}
                                            {s.label}
                                        </div>
                                        <div className="text-base leading-relaxed text-white whitespace-pre-wrap">
                                            {s.value && String(s.value).trim()
                                                ? s.value
                                                : "—"}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex items-center justify-between px-8 py-3 border-t border-virtus-line">
                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint">
                                {footerLeft || ""}
                            </div>
                            {onEdit && (
                                <button
                                    onClick={onEdit}
                                    className="flex items-center gap-2 px-4 py-2 font-mono text-[11px] tracking-[0.24em] uppercase bg-virtus-orange text-black hover:bg-white glow-orange"
                                    data-testid={editTestId}
                                >
                                    <Edit3 size={12} /> EDIT
                                </button>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
